import React, { useLayoutEffect, useRef, useState } from "react";
import { Amenity } from "../../../api/contracts/category-ads";
import { getLucideIcon } from "../../../utils/getLucideIcon";
import { Tooltip } from "../../../components/Tooltip";

const CHIP_GAP = 8; // matches the `gap-2` between chips

// `wrap` lets the chip break a long name across lines and stay within its
// container — used inside the tooltip. The card row keeps `whitespace-nowrap`
// so each chip is one line (the overflow measurement depends on it).
const AmenityChip: React.FC<{ amenity: Amenity; wrap?: boolean }> = ({
  amenity,
  wrap = false,
}) => {
  const Icon = getLucideIcon(amenity.iconName);
  return (
    <div
      className={`flex shrink-0 items-center gap-1.5 overflow-hidden rounded-badge bg-accent-soft px-2 py-1.5 ${
        wrap ? "max-w-full" : ""
      }`}
    >
      <span className="shrink-0 text-accent">
        <Icon className="h-4 w-4" />
      </span>
      <span
        className={`text-xs font-medium text-accent ${
          wrap ? "min-w-0 [overflow-wrap:anywhere]" : "whitespace-nowrap"
        }`}
      >
        {amenity.name}
      </span>
    </div>
  );
};

// forwardRef so it can act as a Radix Tooltip trigger via `asChild`.
const CountChip = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { count: number }
>(({ count, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    className="flex shrink-0 items-center rounded-badge bg-accent-soft px-2 py-1.5"
  >
    <span className="whitespace-nowrap text-xs font-medium text-accent">
      +{count}
    </span>
  </div>
));
CountChip.displayName = "CountChip";

/**
 * Amenity chips on a single row. When they would overflow onto a second row,
 * the trailing slot becomes a `+N` chip counting the hidden amenities.
 * Renders nothing — no DOM, no surrounding gap — when there are no amenities.
 */
export const AmenityChips: React.FC<{
  amenities: Amenity[];
  /** Let the chips run onto a second row instead of collapsing the overflow
   *  into a `+N`. The card grid prefers the taller row to a hidden amenity;
   *  the dialogs, which have their own width, keep the single-row packing. */
  wrap?: boolean;
  trailingAction?: React.ReactNode;
}> = ({ amenities, wrap = false, trailingAction }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleIndices, setVisibleIndices] = useState<number[]>(() =>
    amenities.map((_, i) => i),
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;
    let active = true;

    const recompute = () => {
      if (!active) return;
      const available = container.clientWidth;
      const nodes = Array.from(measure.children) as HTMLElement[];
      const chips = nodes.slice(0, amenities.length);
      const countChip = nodes[nodes.length - 1];

      const fullRow = chips.reduce(
        (sum, chip, i) => sum + chip.offsetWidth + (i > 0 ? CHIP_GAP : 0),
        0,
      );
      if (fullRow <= available) {
        setVisibleIndices(chips.map((_, i) => i));
        return;
      }

      // Doesn't all fit — reserve room for the trailing `+N` chip, then pack
      // chips greedily. A chip too wide to fit is skipped, not a stop point,
      // so one very long amenity doesn't hide every shorter one after it.
      const reserve = CHIP_GAP + countChip.offsetWidth;
      let used = 0;
      const shown: number[] = [];
      for (let i = 0; i < chips.length; i++) {
        const width = chips[i].offsetWidth + (shown.length > 0 ? CHIP_GAP : 0);
        if (used + width + reserve <= available) {
          used += width;
          shown.push(i);
        }
      }
      setVisibleIndices(shown);
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    document.fonts.ready.then(recompute);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [amenities]);

  if (amenities.length === 0 && !trailingAction) return null;

  if (wrap) {
    return (
      <div className="flex flex-wrap items-end gap-2">
        {amenities.map((amenity, i) => (
          <AmenityChip key={i} amenity={amenity} wrap />
        ))}
        {trailingAction}
      </div>
    );
  }

  const visibleSet = new Set(visibleIndices);
  const hiddenAmenities = amenities.filter((_, i) => !visibleSet.has(i));

  return (
    <div ref={containerRef} className="relative flex gap-2 overflow-hidden">
      {/* Off-screen measuring row: every chip + a worst-case `+N` chip. */}
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none absolute -z-10 flex gap-2 opacity-0"
      >
        {amenities.map((amenity, i) => (
          <AmenityChip key={i} amenity={amenity} />
        ))}
        <CountChip count={amenities.length} />
      </div>

      {visibleIndices.map((index) => (
        <AmenityChip key={index} amenity={amenities[index]} />
      ))}
      {hiddenAmenities.length > 0 && (
        <Tooltip
          label={
            <div className="flex flex-wrap gap-1.5">
              {hiddenAmenities.map((amenity, i) => (
                <AmenityChip key={i} amenity={amenity} wrap />
              ))}
            </div>
          }
        >
          <CountChip count={hiddenAmenities.length} />
        </Tooltip>
      )}
    </div>
  );
};
